import { useCallback, useEffect, useId, useRef, useState } from "react";

export const useQuillColorToolbar = (quillRef) => {
  const toolbarId = useId().replace(/:/g, "_");
  const toolbarModuleRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerColor, setPickerColor] = useState("#000000");

  const closePicker = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const applyColor = useCallback(
    (color) => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        return;
      }

      quill.focus();
      quill.format("color", color || false);

      const toolbar = toolbarModuleRef.current;
      const select = toolbar?.container?.querySelector?.("select.ql-color");
      if (select) {
        Array.from(select.querySelectorAll('option[data-custom="true"]')).forEach((option) => option.remove());

        if (!color) {
          select.value = "";
        } else {
          const option = document.createElement("option");
          option.value = color;
          option.setAttribute("data-custom", "true");
          select.appendChild(option);
          select.value = color;
        }
      }
    },
    [quillRef]
  );

  useEffect(() => {
    let cleanup = () => {};
    let isMounted = true;

    const setup = () => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        return false;
      }

      const toolbar = quill.getModule?.("toolbar");
      if (!toolbar) {
        return false;
      }

      toolbarModuleRef.current = toolbar;

      const handleColor = (value) => {
        if (value === "color-picker") {
          const select = toolbar.container?.querySelector?.("select.ql-color");
          if (select) {
            select.value = "";
          }
          const label = toolbar.container?.querySelector?.(
            ".ql-color .ql-picker-label"
          );
          const currentColor = quill.getFormat()?.color ?? pickerColor;
          setPickerColor(typeof currentColor === "string" ? currentColor : "#000000");
          setAnchorEl(label || toolbar.container);
          return;
        }

        applyColor(value || false);
      };

      toolbar.addHandler?.("color", handleColor);

      cleanup = () => {
        if (toolbar?.handlers) {
          toolbar.handlers.color = undefined;
        }
      };

      return true;
    };

    if (!setup()) {
      let attempts = 0;
      const maxAttempts = 40;
      const retry = setInterval(() => {
        if (!isMounted) {
          clearInterval(retry);
          return;
        }

        attempts += 1;
        if (setup() || attempts >= maxAttempts) {
          clearInterval(retry);
        }
      }, 50);

      return () => {
        isMounted = false;
        clearInterval(retry);
        cleanup();
      };
    }

    return () => {
      isMounted = false;
      cleanup();
      toolbarModuleRef.current = null;
    };
  }, [applyColor, pickerColor, quillRef]);

  return {
    toolbarId,
    colorPicker: {
      open: Boolean(anchorEl),
      anchorEl,
      color: pickerColor,
      onSelectColor: (color) => {
        setPickerColor(color);
        applyColor(color);
      },
      onClose: closePicker,
      onClear: () => {
        applyColor(null);
        closePicker();
      },
    },
  };
};